import { useMemo, useRef, useState } from 'react';
import { Box, Flex } from './base';
import { plural, quantidade, reais, reaisCurtos, textoDaVariacao } from './formato';
import { ESCALA, ESTADOS, VIEWBOX } from './mapaDoBrasil';
import { EstadoDoMapa } from './tipos';

// O mapa do período escolhido: cada estado (a UF do cadastro do cliente)
// pintado pelo indicador escolhido nos botões. Os números vêm prontos do MCP
// (painel.py, `por_estado`); a tela só escolhe a cor. Passar o mouse mostra os
// números do estado; clicar num estado que é região do filtro (DF, GO, MG, MT,
// MS) filtra o painel por ela, e clicar de novo tira o filtro. Ao lado, a mesma
// informação em lista, para quem não lê cor.

type Indicador = 'venda' | 'variacao' | 'pendencias' | 'clientes';

const INDICADORES: { nome: Indicador; rotulo: string }[] = [
  { nome: 'venda', rotulo: 'Venda' },
  { nome: 'variacao', rotulo: 'Variação' },
  { nome: 'pendencias', rotulo: 'Pendências' },
  { nome: 'clientes', rotulo: 'Clientes' },
];

// O que o mapa mostra em cada botão, em palavras, com as datas do período
// (o dono, 04/11/2026: "precisa ficar claro o que o mapa mostra"). A lista ao
// lado ganha o mesmo nome na coluna.
function explicacaoDe(ind: Indicador, periodo: string, anterior?: string, dias?: number): string {
  if (ind === 'venda')
    return `Quanto foi vendido em cada estado de ${periodo}. Quanto mais escuro o azul, maior a venda.`;
  if (ind === 'variacao')
    return `O estado vendeu mais ou menos do que antes? Verde: vendeu mais. Vermelho: vendeu menos. Agora é ${periodo}; antes é ${anterior}${dias ? `, os ${dias} dias anteriores` : ''}.`;
  if (ind === 'pendencias')
    return `Pedidos emitidos de ${periodo} que ainda não viraram nota: parados além do prazo ou com erro ao enviar ao ERP. Quanto mais forte o vermelho, mais pedidos travados.`;
  return `Quantos clientes diferentes compraram em cada estado de ${periodo}. Quanto mais escuro o azul, mais clientes.`;
}

const COLUNA: Record<Indicador, string> = {
  venda: 'Venda no período',
  variacao: 'Variação da venda',
  pendencias: 'Pedidos travados',
  clientes: 'Clientes que compraram',
};

// Variação acima disso (para cima ou para baixo) já pinta a cor cheia.
const VARIACAO_CHEIA = 50;

// Estado pequeno demais para ver (e clicar) no mapa inteiro: ganha um círculo
// na mesma cor, com a sigla. O DF tem a maior venda e some sem isso.
const PEQUENOS = ['DF'];
const RAIO = 9000; // em 1/10.000 de grau, a unidade do caminho

const valorDe = (e: EstadoDoMapa, ind: Indicador): number | null => {
  if (ind === 'venda') return e.venda;
  if (ind === 'variacao') return e.variacao_percentual ?? null;
  if (ind === 'pendencias') return e.pendencias;
  return e.clientes;
};

const textoDe = (e: EstadoDoMapa, ind: Indicador): string => {
  if (ind === 'venda') return reaisCurtos(e.venda);
  if (ind === 'variacao') return e.variacao_percentual == null ? 'sem venda antes' : textoDaVariacao(e.variacao_percentual);
  if (ind === 'pendencias') return quantidade(e.pendencias);
  return quantidade(e.clientes);
};

export function MapaDasRegioes({
  estados,
  comparavel,
  regioes,
  regiao,
  onEscolherRegiao,
  periodo,
  anterior,
  dias,
}: {
  estados: EstadoDoMapa[];
  comparavel: boolean;
  // "01/10 a 03/11", o período de comparação e quantos dias tem cada um.
  periodo: string;
  anterior?: string;
  dias?: number;
  // Código → nome da região do filtro ("009" → "DF"); a sigla liga o estado à região.
  regioes: Record<string, string>;
  regiao: string;
  onEscolherRegiao?: (codigo: string) => void;
}) {
  const [indicador, setIndicador] = useState<Indicador>('venda');
  const [sobre, setSobre] = useState<{ uf: string; x: number; y: number } | null>(null);
  const caixa = useRef<HTMLDivElement>(null);
  const porUf = useMemo(() => Object.fromEntries(estados.map((e) => [e.uf, e])), [estados]);
  const regiaoDaUf = useMemo(
    () => Object.fromEntries(Object.entries(regioes).map(([codigo, nome]) => [nome, codigo])),
    [regioes],
  );
  const opcoes = INDICADORES.filter((i) => i.nome !== 'variacao' || comparavel);
  const ativo = opcoes.some((o) => o.nome === indicador) ? indicador : 'venda';
  const maior = Math.max(...estados.map((e) => Math.abs(valorDe(e, ativo) ?? 0)), 1);

  // A cor: uma só cor, de clara a forte, pelo tamanho; na variação, verde para
  // cima e vermelho para baixo; sem número, o cinza do fundo.
  const pintura = (e?: EstadoDoMapa): { fill: string; opacity: number } => {
    const v = e ? valorDe(e, ativo) : null;
    if (!e || v == null || v === 0) return { fill: 'var(--pa-grade)', opacity: 1 };
    if (ativo === 'variacao') {
      const t = Math.min(Math.abs(v), VARIACAO_CHEIA) / VARIACAO_CHEIA;
      return { fill: v > 0 ? 'var(--pa-sobe)' : 'var(--pa-desce)', opacity: 0.2 + 0.8 * t };
    }
    const t = Math.abs(v) / maior;
    return { fill: ativo === 'pendencias' ? 'var(--pa-critico)' : 'var(--pa-dado)', opacity: 0.15 + 0.85 * t };
  };

  const mover = (uf: string, ev: React.MouseEvent) => {
    const r = caixa.current?.getBoundingClientRect();
    if (r) setSobre({ uf, x: ev.clientX - r.left, y: ev.clientY - r.top });
  };
  const clicar = (uf: string) => {
    const codigo = regiaoDaUf[uf];
    if (codigo && onEscolherRegiao) onEscolherRegiao(regiao === codigo ? '' : codigo);
  };

  const lista = [...estados]
    .filter((e) => (valorDe(e, ativo) ?? 0) !== 0 || ativo === 'variacao')
    .filter((e) => ativo !== 'variacao' || e.variacao_percentual != null)
    .sort((a, b) => (valorDe(b, ativo) ?? 0) - (valorDe(a, ativo) ?? 0));
  // O maior número do indicador, no fim da legenda (vem pronto, não é conta).
  const doMaior = [...estados].sort((a, b) => Math.abs(valorDe(b, ativo) ?? 0) - Math.abs(valorDe(a, ativo) ?? 0))[0];
  const maiorTexto = doMaior ? textoDe(doMaior, ativo) : '';
  const estadoSobre = sobre ? porUf[sobre.uf] : undefined;
  const nomeSobre = sobre ? ESTADOS.find((s) => s.uf === sobre.uf)?.nome : '';

  return (
    <Box>
      <Flex role="group" aria-label="Indicador do mapa" wrap="wrap" sx={{ gap: '6px' }} px="16px" pb="12px">
        {opcoes.map((o) => (
          <Box
            key={o.nome}
            as="button"
            type="button"
            aria-pressed={ativo === o.nome}
            onClick={() => setIndicador(o.nome)}
            px="12px"
            h="30px"
            borderRadius="15px"
            fontSize="13px"
            fontWeight={600}
            border="1px solid"
            borderColor={ativo === o.nome ? 'var(--pa-marca)' : 'var(--pa-linha)'}
            bg={ativo === o.nome ? 'var(--pa-marca-suave)' : 'transparent'}
            color={ativo === o.nome ? 'var(--pa-marca-tinta)' : 'var(--pa-tinta-2)'}
            cursor="pointer"
            _focusVisible={{ outline: '2px solid var(--pa-dado)', outlineOffset: '2px' }}
          >
            {o.rotulo}
          </Box>
        ))}
      </Flex>
      <Box px="16px" pb="12px" fontSize="13px" lineHeight="19px" color="var(--pa-tinta-2)" aria-live="polite">
        {explicacaoDe(ativo, periodo, anterior, dias)}
        {onEscolherRegiao && (
          <Box as="span" color="var(--pa-tinta-3)">
            {' '}
            {regiao ? 'Clique no estado destacado para voltar a ver todas as regiões.' : 'Clique em DF, GO, MG, MT ou MS para ver o painel só daquela região.'}
          </Box>
        )}
      </Box>

      <Box display="grid" gridTemplateColumns={{ base: '1fr', md: 'minmax(0, 3fr) minmax(0, 2fr)' }} sx={{ gap: '16px' }} px="16px" pb="16px">
        <Box ref={caixa} position="relative" onMouseLeave={() => setSobre(null)}>
          <Box as="svg" viewBox={VIEWBOX} w="100%" h="auto" maxH="460px" display="block" role="img" aria-label="Mapa do Brasil por estado">
            <g transform={ESCALA}>
              {ESTADOS.map((s) => {
                const e = porUf[s.uf];
                const { fill, opacity } = pintura(e);
                const clicavel = !!regiaoDaUf[s.uf] && !!onEscolherRegiao;
                const escolhido = !!regiao && regiaoDaUf[s.uf] === regiao;
                return (
                  <path
                    key={s.uf}
                    d={s.d}
                    fill={fill}
                    fillOpacity={opacity}
                    stroke={escolhido ? 'var(--pa-tinta)' : 'var(--pa-superficie)'}
                    strokeWidth={escolhido ? 2.5 : 1}
                    vectorEffect="non-scaling-stroke"
                    style={{ cursor: clicavel ? 'pointer' : 'default' }}
                    onMouseMove={(ev) => mover(s.uf, ev)}
                    onClick={() => clicar(s.uf)}
                  >
                    <title>{e ? `${s.nome}: ${textoDe(e, ativo)}` : `${s.nome}: sem venda no período`}</title>
                  </path>
                );
              })}
              {ESTADOS.filter((s) => PEQUENOS.includes(s.uf)).map((s) => {
                const e = porUf[s.uf];
                const { fill, opacity } = pintura(e);
                const escolhido = !!regiao && regiaoDaUf[s.uf] === regiao;
                return (
                  <g
                    key={`${s.uf}-circulo`}
                    style={{ cursor: regiaoDaUf[s.uf] && onEscolherRegiao ? 'pointer' : 'default' }}
                    onMouseMove={(ev) => mover(s.uf, ev)}
                    onClick={() => clicar(s.uf)}
                  >
                    {/* O fundo opaco, para a cor do estado de baixo não vazar. */}
                    <circle cx={s.centro[0]} cy={s.centro[1]} r={RAIO} fill="var(--pa-superficie)" />
                    <circle
                      cx={s.centro[0]}
                      cy={s.centro[1]}
                      r={RAIO}
                      fill={fill}
                      fillOpacity={opacity}
                      stroke={escolhido ? 'var(--pa-tinta)' : 'var(--pa-superficie)'}
                      strokeWidth={escolhido ? 2.5 : 1.5}
                      vectorEffect="non-scaling-stroke"
                    >
                      <title>{e ? `${s.nome}: ${textoDe(e, ativo)}` : `${s.nome}: sem venda no período`}</title>
                    </circle>
                  </g>
                );
              })}
            </g>
            {/* A sigla fica fora da escala invertida, senão sairia de cabeça para baixo. */}
            {ESTADOS.filter((s) => PEQUENOS.includes(s.uf)).map((s) => (
              <text
                key={`${s.uf}-sigla`}
                x={s.centro[0] * 0.0001}
                y={-s.centro[1] * 0.0001 - 1.2}
                textAnchor="middle"
                fontSize="1.1"
                fontWeight={700}
                fill="var(--pa-tinta)"
                stroke="var(--pa-superficie)"
                strokeWidth="0.25"
                paintOrder="stroke"
                pointerEvents="none"
              >
                {s.uf}
              </text>
            ))}
          </Box>

          {/* A dica do estado sob o mouse, com os números que vieram prontos. */}
          {sobre && (
            <Box
              position="absolute"
              left={`${Math.min(sobre.x + 14, (caixa.current?.clientWidth || 400) - 230)}px`}
              top={`${sobre.y + 14}px`}
              w="220px"
              p="10px 12px"
              borderRadius="8px"
              bg="var(--pa-dica)"
              color="var(--pa-dica-tinta)"
              fontSize="12px"
              lineHeight="18px"
              pointerEvents="none"
              zIndex={2}
              boxShadow="0 4px 12px rgba(0,0,0,.18)"
            >
              <Box fontWeight={700} fontSize="13px" mb="4px">
                {nomeSobre} ({sobre.uf})
              </Box>
              {estadoSobre ? (
                <>
                  <div>Venda no período: {reais(estadoSobre.venda)}</div>
                  {comparavel && (
                    <div>
                      Contra {anterior}: {estadoSobre.variacao_percentual == null ? 'sem venda antes' : textoDaVariacao(estadoSobre.variacao_percentual)}
                    </div>
                  )}
                  <div>
                    {plural(estadoSobre.pedidos, 'pedido', 'pedidos')} · {plural(estadoSobre.clientes, 'cliente', 'clientes')}
                  </div>
                  <div>Ticket médio: {reaisCurtos(estadoSobre.ticket_medio)}</div>
                  <div>
                    Travados: {quantidade(estadoSobre.parados.pedidos)} parados · {quantidade(estadoSobre.erros.pedidos)} com erro
                  </div>
                  <div>Sem comprar há 30 a 120 dias: {plural(estadoSobre.clientes_sem_comprar, 'cliente', 'clientes')}</div>
                  {regiaoDaUf[sobre.uf] && onEscolherRegiao && (
                    <Box mt="4px" color="var(--pa-dica-2)">
                      {regiao === regiaoDaUf[sobre.uf] ? 'Clique para ver todas as regiões' : 'Clique para filtrar esta região'}
                    </Box>
                  )}
                </>
              ) : (
                <div>Sem venda no período.</div>
              )}
            </Box>
          )}

          {/* A legenda: a escala com os números de verdade, e o que é o cinza. */}
          <Flex align="center" wrap="wrap" sx={{ gap: '6px 16px' }} mt="10px" fontSize="12px" color="var(--pa-tinta-2)">
            <Flex align="center" sx={{ gap: '8px' }}>
              {ativo === 'variacao' ? (
                <>
                  <span>−{VARIACAO_CHEIA}% ou menos</span>
                  <Box h="10px" w="120px" borderRadius="5px" style={{ background: 'linear-gradient(90deg, var(--pa-desce), var(--pa-grade), var(--pa-sobe))' }} />
                  <span>+{VARIACAO_CHEIA}% ou mais</span>
                </>
              ) : (
                <>
                  <span>{ativo === 'venda' ? 'R$ 0' : '0'}</span>
                  <Box
                    h="10px"
                    w="120px"
                    borderRadius="5px"
                    style={{
                      background: `linear-gradient(90deg, var(--pa-grade), ${ativo === 'pendencias' ? 'var(--pa-critico)' : 'var(--pa-dado)'})`,
                    }}
                  />
                  <span>{maiorTexto}</span>
                </>
              )}
            </Flex>
            <Flex align="center" sx={{ gap: '6px' }}>
              <Box as="i" w="10px" h="10px" borderRadius="3px" bg="var(--pa-grade)" border="1px solid var(--pa-linha)" />
              <span>{ativo === 'variacao' ? 'sem venda para comparar' : ativo === 'pendencias' ? 'nenhum pedido travado' : 'sem venda no período'}</span>
            </Flex>
          </Flex>
        </Box>

        {/* A mesma informação em lista, do maior para o menor. */}
        <Box alignSelf="start" minW={0}>
        <Flex justify="space-between" fontSize="12px" fontWeight={600} color="var(--pa-tinta-3)" pb="6px">
          <span>Estado</span>
          <span>{COLUNA[ativo]}</span>
        </Flex>
        <Box as="ol" listStyleType="none" m={0} p={0} aria-label="Estados pelo indicador escolhido">
          {lista.length === 0 && (
            <Box as="li" fontSize="13px" color="var(--pa-tinta-3)">
              Nada para mostrar neste indicador.
            </Box>
          )}
          {lista.map((e) => {
            const nome = ESTADOS.find((s) => s.uf === e.uf)?.nome || e.uf;
            const codigo = regiaoDaUf[e.uf];
            const escolhido = !!regiao && codigo === regiao;
            const { fill, opacity } = pintura(e);
            return (
              <Box
                as="li"
                key={e.uf}
                display="flex"
                alignItems="center"
                sx={{ gap: '10px' }}
                py="7px"
                borderTop="1px solid var(--pa-grade)"
                fontSize="13px"
                fontWeight={escolhido ? 700 : 400}
              >
                <Box as="i" w="10px" h="10px" borderRadius="3px" flexShrink={0} style={{ background: fill, opacity }} />
                <Box flex="1" minW={0} overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap" title={nome}>
                  {nome}
                </Box>
                <Box fontWeight={600} whiteSpace="nowrap">
                  {textoDe(e, ativo)}
                </Box>
              </Box>
            );
          })}
        </Box>
        </Box>
      </Box>
    </Box>
  );
}
